<script>
    import app from '../data/app.svelte.js';
    import persistent from '../data/persistent.svelte';
    import { currentSession, currentSessionId } from '../data/session.svelte.js';

    import { _ } from 'svelte-i18n';
    import { next, previous, goto, replace } from '../helpers/navigation.svelte.js';
    import { fetchApi } from '../helpers/api.svelte.js';
    import { normalizeTelephone } from '../helpers/phone.js';
    import { escapeValues } from '../helpers/escape.js';

    import Section from '../components/Section.svelte';
    import Navigation from '../components/Navigation.svelte';
    import Button from '../components/Button.svelte';
    import Back from '../components/Back.svelte';
    import Box from '../components/Box.svelte';
    import Legal from '../components/Legal.svelte';
    import Form from '../components/Customer/Form.svelte';

    import Icon from '../components/Icon.svelte';
    import arrowRightIcon from '../assets/icons/arrow-right.svg?raw';
    import aiIcon from '../assets/icons/ai.svg?raw';


    const current = 'welcome';

    /* The round launcher is hidden on this first step; offer the assistant as an
       explicit button instead so it's clear what it does. */
    let chatEnabled = $derived(app.settings.widget.chat?.enabled);

    function openAssistant() {
        // If the early customer form has been filled in, persist it first (same save
        // goNext does on success) so the assistant starts with the customer's details.
        if (currentSession.validate === 'early' && valid) {
            persistent.customer = customer;
            currentSession.customer = customer;
        }

        goto('chat', 'right');
    }

    function goBack() {
        currentSession.customer = {
            firstname: null,
            lastname: null,
            email: null,
            telephone: null
        }

        replace(previous(current), 'left');
    }

    async function goNext() {
        if (!valid) {
            return;
        }

        // Only check customer if early customer data collection is enabled
        if (currentSession.validate === 'early') {
            const form = new FormData();
            form.append('session', currentSessionId);
            form.append('client', currentSession.client);
            form.append('salon', currentSession.salon);
            form.append('guid', persistent.guid);
            form.append('name[firstname]', customer.firstname);
            form.append('name[lastname]', customer.lastname);
            form.append('email', customer.email);
            form.append('telephone', normalizeTelephone(customer.telephone));

            const data = await fetchApi({
                endpoint: 'OnlineAppointment.Remote.Customer/check',
                data: form
            });

            if (data.status === 'blocked') {
                replace('error/customer-blocked', 'right');
                return;
            }
            else if (data.status === 'rejected') {
                replace('error/customer-rejected', 'right');
                return;
            }
            else if (data.status === 'error') {
                replace('error/customer-failed', 'right');
                return;
            }
            else if (data.status === 'validate') {
                currentSession.pendingCustomer = customer;

                goto('validate', 'right');
                return;
            }
            else if (data.status === 'ok') {
                persistent.customer = customer;
                currentSession.customer = customer;

                goto(next(current), 'right');
                return;
            }
        }

        /* If we have a dedicated customer step, go to next step */
        else {
            goto(next(current), 'right');
        }
    }


    let privacyStatementUrl = $derived(app.settings.terms.privacy || app.salon.website);

    /* Determine default values for customer form */

    let customer = {
        firstname: currentSession.customer.firstname || persistent.customer.firstname || '',
        lastname: currentSession.customer.lastname || persistent.customer.lastname || '',
        email: currentSession.customer.email || persistent.customer.email || '',
        telephone: currentSession.customer.telephone || persistent.customer.telephone || ''
    };

    let valid = $state(false);
    let preset = $state(false);

    function onChange(value) {
        customer = value;
        valid = value.firstname && value.lastname && value.email && value.telephone;
    }


    /* We have a preset customer, so skip form and enable next button */

    if (customer.firstname || customer.lastname || customer.email || customer.telephone) {
        preset = true;
        valid = true;
    }

    /* We don't have a form, so just enable next button */

    if (currentSession.validate !== 'early') {
        valid = true;
    }

    /* User wants to reset the form */

    function onReset() {
        customer = {
            firstname: '',
            lastname: '',
            email: '',
            telephone: ''
        };

        valid = false;
        preset = false;
    }


</script>

<svelte:head>
    <title>{$_('appointment_title')} - {$_('welcome')}</title>
</svelte:head>

<Section title={$_('welcome')}>
    {#snippet back()}
        {#if previous(current)}
            <Back onclick={goBack} />
        {/if}
    {/snippet}

    <br>

    {#if currentSession.validate === 'early'}
            {#if preset}
                <Box>
                    <h2>{@html $_('welcome_back', { values: escapeValues(customer) })}</h2>

                    <p>
                        <a onclick={onReset}>
                            {$_('not_you', { values: customer })}
                        </a>
                    </p>
                </Box>
            {:else}
                <Box>
                    <!-- <h2>{$_('your_details')}</h2> -->
                    <p>{$_('appointment_details_required')}</p>

                    <Form {...customer} {onChange} onSubmit={goNext} />
                </Box>
            {/if}

            <br>
        {/if}

        <Legal>
            {@html $_('privacy_consent', { values: { url: privacyStatementUrl } })}
        </Legal>

        <Navigation>
            {#if chatEnabled}
                <Button class="assistant" onclick={openAssistant}>
                    <Icon data={aiIcon} />
                    {$_('chatbot_button')}
                </Button>
                <span class="or">{$_('or')}</span>
            {/if}
            <Button color="accent" onclick={goNext} disabled={!valid}>
                {$_('next')}
                <Icon data={arrowRightIcon} />
            </Button>
        </Navigation>
</Section>


<style>

    h2 {
        margin-bottom: 0.5em;
        font-family: var(--theme-alt-font-family);
        font-weight: normal;
        font-size: 2em;
    }

    a {
        color: var(--text-color);
        cursor: pointer;
        text-decoration: underline;
    }

    /* "of"/"or" separator between the assistant and next buttons */
    .or {
        align-self: center;
        color: var(--text-disabled);
    }

    :global(.button.assistant svg) {
        scale: 120%;
        margin: 0 -5px;
        fill: var(--accent-background);
    }

    @media (prefers-color-scheme: dark) {
        :global(.button.assistant svg) {
            fill: oklch(from var(--accent-background) max(l, .7) min(c, .06) h);
        }
    }

</style>